import React, { useEffect, useState } from "react";
import { useChatbotStore, useChatStateStore, useFormStore, useMessagesStore, useUserStore } from "../../zustand/store";
import { ChatBubble } from "../ChatBubble";
import { useSendMessageWithWebSocket } from "../../services/queries/chatbotQueries";
import { createInputComponent } from "./InputFactory";
import { validatePreQuestionConfig, normalizePreQuestionConfig } from "./types";
import { saveFastfiveContext } from "../../utils/fastfiveContext";

/**
 * 리팩토링된 PreQuestion 컴포넌트
 * - 설정 기반으로 동적 Input 컴포넌트 생성
 * - Factory Pattern으로 Input 타입 관리
 * - 확장 가능한 구조
 *
 * flow:
 *   - 'product' (기본): 모든 답변 완료 시 WS로 requestMessage 전송 → 상품 추천 흐름
 *   - 'formOnly': WS 호출 없이 곧바로 리드폼을 띄움 (fastfive 인테리어/사옥 구축 시나리오)
 */
const PreQuestionRefactored = ({
  config = null, // 외부에서 설정을 주입받을 수 있음
  onComplete = null, // 완료 시 콜백
  flow = null,    // 'product' | 'formOnly' | null(= 'product')
}) => {
  const { chatbot } = useChatbotStore();
  const {
    isPreAnswered,
    setIsPreAnswered,
    setIsChatbotPending,
    setIsStreaming,
    setIsFormSubmitted,
  } = useChatStateStore();
  const { mutate: sendMessageWithWebSocket } = useSendMessageWithWebSocket();
  const { partnerId } = useUserStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [preAnswers, setPreAnswers] = useState([]);
  const [scenarioConfig, setScenarioConfig] = useState(null);
  const { preAnswersObject, setPreAnswersObject } = useFormStore();

  // 설정 초기화
  useEffect(() => {
    const rawConfig = config || chatbot?.preQuestion;
    
    if (!rawConfig) {
      console.warn('PreQuestion: 설정 데이터가 없습니다');
      return;
    }

    // 설정 정규화 및 검증
    const normalizedConfig = normalizePreQuestionConfig(rawConfig);
    const validation = validatePreQuestionConfig(normalizedConfig);
    
    if (!validation.isValid) {
      console.error('PreQuestion 설정 오류:', validation.errors);
      return;
    }

    if (validation.warnings.length > 0) {
      console.warn('PreQuestion 설정 경고:', validation.warnings);
    }

    setScenarioConfig(normalizedConfig);
  }, [config, chatbot?.preQuestion]);

  // 모든 질문이 완료되었을 때 처리
  useEffect(() => {
    if (!scenarioConfig) return;

    if (scenarioConfig.answersLength === preAnswers.length && scenarioConfig.questions.length > 0) {
      // 시나리오와 사전답변을 브라우저에 남긴다. 새 브라우저 세션으로 돌아와도
      // 리드에 productType 과 userCount 가 실리도록. (fastfive 가 아니면 no-op)
      // 최신 값을 읽어야 하므로 스토어에서 직접 가져온다.
      saveFastfiveContext(partnerId, {
        scenarioKey: useChatStateStore.getState().fastfiveScenarioId,
        preAnswersObject: useFormStore.getState().preAnswersObject,
      });

      if (flow === 'formOnly') {
        // WS 호출 없이 곧바로 리드폼을 띄움 (인테리어/사옥 구축 시나리오)
        setCurrentStep(0);
        setPreAnswers([]);
        setIsPreAnswered(true);
        setIsFormSubmitted('ready');
      } else {
        setIsStreaming(true);

        // 메시지 전송
        sendMessageWithWebSocket({
          messageInput: scenarioConfig.requestMessage,
          isChatbot: false,
          preQuestionAnswers: preAnswers,
        });

        setIsChatbotPending(true);
        setCurrentStep(0);
        setPreAnswers([]);
        setIsPreAnswered(true);
      }

      // 외부 콜백 호출
      if (onComplete) {
        onComplete(preAnswers, scenarioConfig);
      }
    }
  }, [preAnswers, scenarioConfig, sendMessageWithWebSocket, setIsStreaming, setIsChatbotPending, setIsPreAnswered, setIsFormSubmitted, flow, onComplete, partnerId]);

  // 답변 처리 핸들러
  const handleAnswer = (answer, order) => {
    setPreAnswers(prev => [...prev, answer]);
    
    // PreQuestion 응답을 formStore에 인덱스 기반으로 저장
    setPreAnswersObject({
      ...preAnswersObject,
      [order]: answer,
    });
    
    // SDK에 메시지 전송
    const targetWindow = window.parent;
    const preInputSubmittedState = {
      preInputSubmittedState: true,
      [`step${order + 1}`]: answer,
    };
    targetWindow.postMessage(preInputSubmittedState, '*');
  };

  // 다음 단계로 이동
  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
  };

  // 현재 질문의 설정을 정규화
  const getCurrentQuestionConfig = () => {
    if (!scenarioConfig || currentStep >= scenarioConfig.questions.length) {
      return null;
    }
    
    const question = scenarioConfig.questions[currentStep];
    return question;
  };

  if (!scenarioConfig || isPreAnswered) {
    return null;
  }

  const currentQuestionConfig = getCurrentQuestionConfig();
  
  if (!currentQuestionConfig) {
    return null;
  }

  // 현재 질문에 해당하는 Input 컴포넌트 생성
  const InputComponent = createInputComponent(currentQuestionConfig, {
    order: currentStep,
    question: currentQuestionConfig.question,
    onAnswer: handleAnswer,
    onNext: handleNext,
  });

  return (
    <div style={{ width: '100%', height: 'fit-content' }}>
      <ChatBubble message={{ message: currentQuestionConfig.question, isChatbot: true }} />
      {InputComponent}
    </div>
  );
};

export default PreQuestionRefactored;
